<template>
  <div class="relative flex flex-col justify-between w-72">
    <slot></slot>
    <div
      class="bg-raster-pattern raster-border rounded flex flex-grow relative max-h-[50%] overflow-hidden"
      :class="templateStyles(filter.getters.wallSelection.value)"
    >
      <div class="flex items-center justify-center h-56">
        <PreviewNote v-for="(note, index2) in wall.preview" :key="index2" :preview-note="note" :length="wall.preview.length" />
        <div v-if="wall.preview.length === 0" class="w-full">
          <p class="text-slate-white font-bold text-lg">{{ t('nopreview') }}</p>
        </div>
      </div>
    </div>

    <div class="flex flex-col text-left px-1 pt-4 pb-2" :style="wallItemStyle(false)">
      <p class="text-white text-2xl font-bold whitespace-nowrap overflow-hidden bg-text-fade-middle mb-2">{{ wall.details.title }}</p>
      <div class="flex items-center my-3 -ml-1 overflow-hidden flex-wrap">
        <div
          v-for="(subject, index2) in wall.details.subjects.slice(0, 3)"
          :key="index2"
          class="flex items-center mr-2 rounded-2xl"
          :style="`background-color: ${color.actions.selectColor(subject.name).background};`"
        >
          <span v-if="subject.icon" class="mx-1.5 flex justify-center">{{ subject.icon }}</span>
          <span v-if="subject.name" class="mr-3" :style="`color: ${color.actions.selectColor(subject.name).text};`">{{ subject.name }}</span>
        </div>
        <p
          v-if="wall.details.subjects.length - 3 > 0"
          class="bg-raster text-slate-bright p-1 rounded-2xl w-6 h-6 flex justify-center items-center text-sm"
        >
          {{ `+${wall.details.subjects.length - 3}` }}
        </p>
        <p v-if="wall.details.subjects.length === 0" class="bg-raster flex items-center rounded-2xl text-white px-2 py-1 text-sm">
          {{ t('nosubjects') }}
        </p>
        <p v-if="feideSharing().length > 0" class="bg-blue-middle flex items-center rounded-2xl text-white px-2 py-px text-sm mx-1">
          {{ t('sharedinfeide') }}
        </p>
        <p v-if="canvasSharing().length > 0" class="bg-blue-dark flex items-center rounded-2xl text-white px-2 py-px text-sm font-bold mx-1">
          {{ t('sharedincanvas') }}
        </p>
      </div>
      <p class="text-slate-bright text-md whitespace-nowrap overflow-hidden bg-text-fade mt-2">{{ wall.details.description }}</p>
      <div class="text-slate-bright text-sm flex items-center -mb-2" :style="wallItemStyle(false)">
        <p class="font-bold text-md">{{ wall.users.owner.username }}</p>
        <p class="flex items-center mx-1 text-2xl">&#8226;</p>
        <p class="font-thin">{{ getEditedLabel(wall) }}</p>
      </div>
    </div>

    <div v-if="wall.status.readOnly || wall.authorisedAccessOnly" class="absolute top-6 left-5 flex">
      <div v-if="wall.status.readOnly" class="p-1 mx-1 w-10 h-10 bg-blue-middle rounded-full flex justify-center items-center z-10">
        <img class="p-1.5 invert" :src="AccessLock" :alt="t('aria-access-locked')" />
      </div>
      <div v-if="wall.authorisedAccessOnly" class="p-1 mx-1 w-10 h-10 lp-bg-red rounded-full flex justify-center items-center z-10">
        <img class="p-1.5 invert" :src="UserLock" :alt="t('aria-access-authonly')" />
      </div>
    </div>

    <div
      v-if="wallStore.getters.templates.value.find((t) => t._id === wall._id)"
      class="relative h-7 w-auto mb-2 overflow-auto overflow-x-hidden text-white flex justify-center items-center z-1 cursor-pointer regular-btn rounded-2xl"
    >
      <p class="text-sm">{{ `${t('sharedby')} ${wall.users.owner.username} ${sharedFrom(wall)}` }}</p>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { ref, onUnmounted, computed, PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { FilterType, WallSelectionType, ScreensizeType, PermissionLevel, GroupType } from '@/constants'
  import { Wall, Sharing } from '@/common/classes'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useColorStore from '@/composables/colors'
  import useFilterStore from '@/composables/filter'
  import useCommonStore from '@/composables/common'

  import PreviewNote from './PreviewNote.vue'

  import UserLock from '@/assets/icons/fontawesome/user-lock-solid.svg'
  import AccessLock from '@/assets/icons/fontawesome/file-lock-regular.svg'

  const messages = {
    no: {
      newwall: 'Ny tavle',
      time: 'laget',
      updated: 'oppdatert',
      created: 'Laget',
      edited: 'Redigert',
      nowalls: 'Du har ikke laget eller deltatt på en tavle enda.',
      accessdenied: 'Du har ikke adgang til denne tavlen, spør eieren om adgang',
      share: 'Del',
      copywallpin: 'Kopier PIN til tavle',
      copywalllink: 'Kopier lenke til tavla',
      includingsubject: ': emne',
      sharedby: 'Delt av',
      in: 'i',
    },
    en: {
      newwall: 'New wall',
      time: 'time',
      updated: 'update',
      created: 'Created',
      edited: 'Edited',
      nowalls: 'You have not joined or created a wall yet.',
      accessdenied: "You don't have access to this wall, ask the owner to allow access",
      share: 'Share',
      copywallpin: 'Copy wall PIN',
      copywalllink: 'Copy wall link',
      includingsubject: ': subject',
      sharedby: 'Shared by',
      in: 'in',
    },
  }

  const wallWrapper = ref<HTMLElement | null>(null)

  const color = useColorStore()
  const filter = useFilterStore()
  const common = useCommonStore()
  const userStore = useUserStore()
  const wallStore = useWallStore()

  const props = defineProps({
    wall: { type: Object as PropType<Wall>, required: true },
  })

  const emit = defineEmits<{
    (e: 'selectWall'): void
  }>()

  const { t } = useI18n({ messages })

  onUnmounted(() => {
    window.removeEventListener('resize', () => ({}))
  })

  function templateStyles(type: string) {
    return type === WallSelectionType.TEMPLATES ? '-mt-7' : ''
  }

  function sharedFrom(wall: Wall) {
    const user = userStore.getters.user.value
    if (user) {
      const shares = wall.users.shares
      const userId = user._id
      for (const share of shares) {
        if (user.profile.groups.some((g) => g.originalId === share.group_id)) {
          switch (share.groupType) {
            case GroupType.CANVAS:
              return t('in') + ' Canvas'
            case GroupType.ZOOM:
              return t('in') + 'Zoom'
            case GroupType.DATAPORTEN:
              return t('in') + 'Feide'
            default:
              return '' // link / pin
          }
        }
      }
    } else return ''
  }

  function getEditedLabel(wall: Wall) {
    return filter.getters.selectedWallFilterId.value === FilterType.TIME
      ? `${t('created')} ${common.actions.getCleanTimestamp(wall.status.created)}`
      : `${t('edited')} ${common.actions.getCleanTimestamp(wall.status.lastOpened)}`
  }

  function wallItemStyle(height: boolean, container = false, last = false) {
    if (wallWrapper.value) {
      let itemsPerRow
      switch (common.getters.screensizeType.value) {
        case ScreensizeType.tablet:
          itemsPerRow = 2
          break
        case ScreensizeType.phone:
          itemsPerRow = 1
          break
        default:
          itemsPerRow = 3
      }
      const paddingCompensation = !container ? 20 : -5 // compensate horizontal padding for each item in calculation
      const value = (wallWrapper.value.clientWidth - 100) / itemsPerRow - paddingCompensation
      // setting both height and width prevents the relative width to skew the first element
      let heightValue = height ? 'height: ' + value + 'px;' : ''
      let maxHeightValue = 'max-height: ' + 1.5 * value + 'px;'
      const lastSpacing = common.getters.screensizeType.value === ScreensizeType.phone && last ? 'margin-bottom: 7rem;' : ''
      return 'width: ' + value + 'px;' + heightValue + maxHeightValue + lastSpacing
    }
  }

  function feideSharing() {
    return props.wall.users.shares.filter((s: Sharing) => s.groupType === GroupType.DATAPORTEN && s.permissionLevel !== PermissionLevel.Copy)
  }

  function canvasSharing() {
    return props.wall.users.shares.filter((s: Sharing) => s.groupType === GroupType.CANVAS && s.permissionLevel !== PermissionLevel.Copy)
  }
</script>

<style scoped>
  #walls-wrapper {
    height: 87vh;
  }
</style>
